"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import { useAuth } from "@/providers/AuthProvider";
import { Header } from "@/app/Header";
import { Loading } from "@/components/Loading";
import ErrorMessage from "@/components/ErrorMessage";
import PermissionErrorMessage from "@/components/PermissionErrorMessage";
import CoverPage from "./CoverPage";
import SwitchSchool from "./user/school/SwitchScool";
import UserProfileEdit from "./user/profile/UserProfileEdit";

export default function ProtectedLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const pathname = usePathname();
    const isLoginPage = pathname === "/login";
    const isAdminPage = pathname.includes("/admin");

    const {
        user,
        userProfileCompleted,
        userError,
        sessionStatus,
        userLoading,
    } = useAuth();

    const permisionsLoading = sessionStatus !== "authenticated" || userLoading;
    const appPermission = user && user.is_active && !userError;
    const adminPermission = user && user.is_app_admin;

    const [showCoverPage, setShowCoverPage] = useState(false);
    const [showSwitchSchoolPage, setShowSwitchSchoolPage] = useState(false);
    const [showUserProfileEdit, setShowUserProfileEdit] = useState(false);
    const [wasLoginPage, setWasLoginPage] = useState(false);

    useEffect(() => {
        setShowUserProfileEdit(!userLoading && !userProfileCompleted && !isLoginPage);
    }, [userLoading, userProfileCompleted, isLoginPage]);

    useEffect(() => {
        setShowCoverPage(isLoginPage);
        setWasLoginPage(prev => prev || isLoginPage);
        setShowSwitchSchoolPage(wasLoginPage && !isLoginPage);
    }, [isLoginPage, wasLoginPage]);

    const pageContent = (
        <>
            {!isLoginPage &&
            (!appPermission || (isAdminPage && !adminPermission)) ? (
                <>
                    {permisionsLoading ? (
                        <Loading />
                    ) : !appPermission ? (
                        <ErrorMessage
                            title="ההתחברות נכשלה"
                            message={[
                                "אין לך הרשאת גישה למערכת.",
                                `אנא פנה למנהל.ת ביה&quot;ס לבירור.`,
                            ]}
                        />
                    ) : isAdminPage && !adminPermission ? (
                        <PermissionErrorMessage />
                    ) : (
                        <></> // Unreachable
                    )}
                </>
            ) : user && showUserProfileEdit ? (
                <UserProfileEdit
                    user={user}
                    onClose={() => {
                        setShowUserProfileEdit(false);
                    }}
                />
            ) : (
                children
            )}
        </>
    );

    return (
        <div className="page">
            {!isLoginPage && !showCoverPage && <Header />}
            <div className="main">
                {showCoverPage ? (
                    <CoverPage
                        onClick={() => {
                            setShowCoverPage(false);
                        }}
                    />
                ) : userLoading ? (
                    <Loading />
                ) : showSwitchSchoolPage &&
                  (user?.teachers?.length ?? 0) > 1 ? (
                    <SwitchSchool
                        onSwitch={() => {
                            setShowSwitchSchoolPage(false);
                        }}
                    />
                ) : (
                    pageContent
                )}
            </div>
        </div>
    );
}
